import { useInfiniteQuery } from '@tanstack/react-query';
import { getUserLikedEvents } from '@/apiEndpoints';
import EventCard from './EventCard';
import EmptySearch from './EmptySearch';
import { IconArrowRight as ArrowRight } from '@tabler/icons-react';
import { btnPrimary, eyebrow, sectionTitle } from '@/components/ui/design';

export default function LikesTab() {
    const {
        data,
        isLoading,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
        refetch,
    } = useInfiniteQuery({
        queryKey: ['likedEvents'],
        queryFn: ({ pageParam = 1 }) => getUserLikedEvents({ page: pageParam, limit: 12 }),
        getNextPageParam: (lastPage: any) => {
            const page = lastPage?.pagination?.page || 1;
            const totalPages = lastPage?.pagination?.totalPages || 1;
            return page < totalPages ? page + 1 : undefined;
        },
        initialPageParam: 1,
    });

    const likedEvents = (data?.pages || [])
        .flatMap((page: any) => page?.data || [])
        .map((like: any) => like?.event ? {
            ...like.event,
            _id: like.event._id || like.eventId,
            likeStats: { totalLikes: 0, isLikedByCurrentUser: true, eventId: like.event._id || like.eventId },
        } : null)
        .filter((event: any) => !!event?._id);

    return (
        <div className="w-full">
            <p className={eyebrow}>Saved for later</p>
            <h1 className={`${sectionTitle} mt-3 mb-8 md:mb-10`}>Liked Events</h1>

            {isLoading ? (
                <div className="flex h-40 items-center justify-center text-base text-grey40 dark:text-nightMuted">
                    <div className="flex flex-col items-center">
                        <div className="mb-3 h-8 w-8 animate-spin rounded-full border-2 border-grey90 border-t-grey10 dark:border-white/10 dark:border-t-white"></div>
                        <span>Loading events...</span>
                    </div>
                </div>
            ) : likedEvents.length === 0 ? (
                <div className="flex min-h-[50vh] items-center justify-center rounded-[28px] bg-grey99 px-6 py-16 dark:bg-white/[0.04]">
                    <div className="flex flex-col items-center justify-center text-center">
                        <EmptySearch />
                        <div className="mt-2 text-[clamp(24px,3.5vw,36px)] font-bold tracking-[-0.04em] text-grey10 dark:text-white">No liked events yet</div>
                        <div className="mt-2 text-base text-grey40 dark:text-nightMuted">Events you like will show up here.</div>
                    </div>
                </div>
            ) : (
                <>
                    <div className="mb-2 text-sm font-semibold text-grey40 dark:text-nightMuted">
                        {likedEvents.length} results
                    </div>
                    <div className="mb-8 grid grid-cols-1 content-start sm:-mx-4 sm:grid-cols-2 lg:grid-cols-3">
                        {likedEvents.map((event: any) => (
                            <EventCard
                                key={event._id}
                                event={event}
                                userInfo={undefined}
                                organizerInfo={undefined}
                                onLikeChange={(_id, _likes, isLiked) => { if (!isLiked) refetch(); }}
                            />
                        ))}
                    </div>
                    {hasNextPage && (
                        <div className="mb-12 flex justify-center">
                            <button
                                className={`${btnPrimary} group h-14 pl-7 pr-2 text-base`}
                                onClick={() => fetchNextPage()}
                                disabled={isFetchingNextPage}
                            >
                                {isFetchingNextPage ? 'Loading...' : 'See More'}
                                <span className="flex h-10 w-10 items-center justify-center rounded-full bg-white/15 transition-transform group-hover:translate-y-0.5 dark:bg-black/10">
                                    <ArrowRight className="h-4 w-4 rotate-90" />
                                </span>
                            </button>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
